import { Box, type BoxProps } from '@mui/material'
import React, { type FC } from 'react'
import { observer } from 'mobx-react-lite'
import { SwitchField, Typography } from 'shared/ui'
import { TimePicker } from 'shared/ui/DatePicker'
import { type ILabelProps } from 'shared/ui/Label'
import { useSendingHoursContext } from 'shared/ui/Schedule/ui/SendingHours/context/SendingHoursContext'
import { SelectField } from 'shared/ui/Select/SelectField/SelectField'
import styles from './styles.module.scss'

export type SendingHoursContentProps = {
  expandedBoxProps?: BoxProps
}

export const SendingHoursContent: FC<SendingHoursContentProps> = observer(
  ({ expandedBoxProps }) => {
    const {
      isSendingHoursDisabled,
      isSendingHours,
      isEnabled,
      toggleSendingHours,
      setStartDate,
      startDate,
      setStopDate,
      stopDate,
      dropdownId,
      selectedAction,
      actionItems,
      setAction,
      filteredPassedTime,
      filteredRemainingTime,
    } = useSendingHoursContext()

    const label: ILabelProps['label'] = {
      leftIcon: 'arrowExpandHorizontal',
      text: 'Restrict sending hours',
    }

    return (
      <>
        <Box padding={1} borderBottom={'1px solid var(--border-neutral-default)'}>
          <SwitchField
            value={isSendingHours}
            onClick={toggleSendingHours}
            switchProps={{
              disabled: isSendingHoursDisabled,
              tooltipProps: {
                width: 257,
                visibility: isSendingHoursDisabled ? 'visible' : 'hidden',
                label: 'Disabled when “Use contact timezone” or “Advanced scheduling” is active',
                placement: 'top',
              },
            }}
            labelProps={{
              label,
              questionTooltipProps: {
                label: 'Limit message delivery to specific hours',
              },
            }}
          />
        </Box>

        {isEnabled && (
          <>
            <Box
              padding={2}
              display={'flex'}
              gap={2}
              sx={{ background: 'var(--background-neutral-subtle)' }}
              {...expandedBoxProps}
            >
              <div className={styles.picker}>
                <TimePicker
                  onChange={setStartDate}
                  selected={startDate}
                  parentsDropdownId={[dropdownId]}
                  filteredRemainingTime={filteredRemainingTime}
                  textFieldPlaceholderProps={{
                    placeholder: 'Start sending at',
                  }}
                />
              </div>
              <div className={styles.picker}>
                <TimePicker
                  onChange={setStopDate}
                  selected={stopDate}
                  parentsDropdownId={[dropdownId]}
                  filteredPassedTime={filteredPassedTime}
                  textFieldPlaceholderProps={{
                    placeholder: 'Stop sending at',
                  }}
                />
              </div>
            </Box>
            <Box
              padding={2}
              display={'flex'}
              flexDirection={'column'}
              gap={2}
              sx={{ background: 'var(--background-neutral-subtle)' }}
              {...expandedBoxProps}
            >
              <Typography
                variant={'body-md-regular'}
                color={'var(--content-primary-tertiary)'}
                ariaLabel={'SendingHoursConditions'}
              >
                If some messages aren’t sent by the end time
              </Typography>
              <SelectField
                value={selectedAction}
                setValue={(data) => {
                  setAction(data)
                }}
                items={actionItems}
                parentsDropdownId={[dropdownId]}
              />
            </Box>
          </>
        )}
      </>
    )
  }
)
